import _ from 'lodash';

import { CANNABIS_TYPES } from 'shared/core/constants/product';
import { getComplianceConfig } from 'shared/core/helpers/compliance';
import { MG_TO_G } from 'shared/constants';
import { safeToBig } from 'shared/helpers/utils_math';
import {
  getPotencyValues,
  getProductWeight,
  getEquivalent,
  getProductNetWeightInGrams,
  getProductVolumeInGrams,
} from 'shared/helpers/products';

import { CONCENTRATE_TYPES, EDIBLE_TYPES, TINCTURE_TYPES, TOPICAL_TYPES, GRAMS_PER_OUNCE } from './constants';

import {
  getStateLimits,
  getTypeLimits,
  getLimitMessage,
  getLimitForType,
  isFlower,
  isConcentrate,
  isEdible,
  isTincture,
  isTopical,
  isWeightedProduct,
  isLiquidTypeProduct,
  isSolidEdibleTypeProduct,
  formatDisplayedType,
} from './utils';

import { checkSolidEdibleNetWeight } from './check-solid-edible-net-weight';
import { checkLiquidEdibleVolume } from './check-liquid-edible-volume';

export const shouldCombine = (limits, medical) => {
  if (medical) {
    return limits.medUsesEquivalencyCalculator;
  }

  return limits.usesEquivalencyCalculator;
};

/**
 * This function is used to check the current product type and its subcategory
 * against any type maps that exist in a state/region's compliance configuration
 */
const isTypeMapType = (typeFromCartProduct = '', subcategoryFromCartProduct = '', limits, category) => {
  const categories = _.get(limits, `typeMap.${category}.categories`, []);
  const subcategories = _.get(limits, `typeMap.${category}.subcategories`, []);
  const allCategoriesForType = [...categories, ...subcategories];

  return (
    _.includes(allCategoriesForType, typeFromCartProduct) ||
    _.includes(allCategoriesForType, subcategoryFromCartProduct)
  );
};

const isIgnoreTypeMapType = (typeFromCartProduct, subcategoryFromCartProduct, limits, category) => {
  const ignoredCategories = _.get(limits, `ignoreTypeMap.${category}.categories`, []);
  const ignoredSubcategories = _.get(limits, `ignoreTypeMap.${category}.subcategories`, []);
  const allIgnoredCategoriesForType = [...ignoredCategories, ...ignoredSubcategories];

  return (
    _.includes(allIgnoredCategoriesForType, typeFromCartProduct) ||
    _.includes(allIgnoredCategoriesForType, subcategoryFromCartProduct)
  );
};

export const getMultiplier = (productType, limits, medical, subcategory = '') => {
  if (shouldCombine(limits, medical)) {
    const multType = medical ? 'med' : 'rec';
    // Some states have nested multipliers specific for rec/med
    // Use the nested multiplier if it exists
    // If not, then use the non-nested multiplier
    if (isFlower(productType) && !isIgnoreTypeMapType(productType, subcategory, limits, 'Flower')) {
      return 1;
    }

    if (limits.usesSolidEdibleTypes && isSolidEdibleTypeProduct(productType, subcategory)) {
      return _.get(limits.solidNetWeightMultiplier, multType, limits.solidNetWeightMultiplier) || 0;
    }
    if (limits.usesLiquidTypes && isLiquidTypeProduct(limits, productType, subcategory)) {
      return _.get(limits.liquidNetWeightMultiplier, multType, limits.liquidNetWeightMultiplier) || 0;
    }
    if (_.includes(CONCENTRATE_TYPES, productType) || isTypeMapType(productType, subcategory, limits, 'Concentrate')) {
      return _.get(limits.concentrateMultiplier, multType, limits.concentrateMultiplier) || 0;
    }
    if (_.includes(EDIBLE_TYPES, productType) || isTypeMapType(productType, subcategory, limits, 'Edible')) {
      return _.get(limits.edibleMultiplier, multType, limits.edibleMultiplier) || 0;
    }
    if (_.includes(TINCTURE_TYPES, productType) || isTypeMapType(productType, subcategory, limits, 'Tincture')) {
      return _.get(limits.tinctureMultiplier, multType, limits.tinctureMultiplier) || 0;
    }
    if (_.includes(TOPICAL_TYPES, productType) || isTypeMapType(productType, subcategory, limits, 'Topicals')) {
      return _.get(limits.topicalMultiplier, multType, limits.topicalMultiplier) || 0;
    }
  }

  if (isFlower(productType)) {
    return 1;
  }

  return 0;
};

export const cartHasMix = (cart) => {
  const { limits } = cart;
  const productTypes = _.uniq(_.map(cart.orders, ({ product }) => product.type));
  const productSubcategories = _.uniq(_.map(cart.orders, ({ product }) => product.subcategory));
  const allTypes = [...productTypes, ...productSubcategories];
  const hasConcentrates =
    !!_.find(allTypes, isConcentrate) || !!_.find(allTypes, (type) => isTypeMapType(type, type, limits, 'Concentrate'));
  const hasFlower =
    !!_.find(allTypes, isFlower) || !!_.find(allTypes, (type) => isTypeMapType(type, type, limits, 'Flower'));
  const hasEdibles =
    !!_.find(allTypes, isEdible) || !!_.find(allTypes, (type) => isTypeMapType(type, type, limits, 'Edible'));
  const hasTinctures =
    !!_.find(allTypes, isTincture) || !!_.find(allTypes, (type) => isTypeMapType(type, type, limits, 'Tincture'));
  const hasTopicals =
    !!_.find(allTypes, isTopical) || !!_.find(allTypes, (type) => isTypeMapType(type, type, limits, 'Topicals'));

  return _.compact([hasFlower, hasConcentrates, hasEdibles, hasTinctures, hasTopicals]).length > 1;
};

export const getProductTHCInGrams = ({ product, quantity }) => {
  const { defaultWeight } = product;
  return safeToBig(quantity)
    .times(Number(getPotencyValues(product).THC) || defaultWeight)
    .div(MG_TO_G);
};

export const getFlowerEquivalent = (order, medical, limits) => {
  const { product, option, quantity } = order;
  const { defaultWeight, type, subcategory } = product;
  const multiplier = product.multiplier ?? 1;
  const standardEquivalent = getEquivalent(product, option, quantity);
  const recEquivalent = getEquivalent(product, option, quantity, 'recEquivalent');
  /*
   * This logic is just to allow IL dispensaries to pass the standardEquivalent field on products
   * even though it shouldn't be used due to their config using equivalency for medical but not recreational
   * orders. IL is the only state config that includes the limits.ignoreStandardEquivalentField
   * - Mack H. 11/5/21
   */
  const allowStandardEquivalentFieldUse = !limits?.ignoreStandardEquivalentField;

  if (_.isFinite(recEquivalent) && !medical) {
    return recEquivalent;
  }
  if (_.isFinite(standardEquivalent) && allowStandardEquivalentFieldUse) {
    return standardEquivalent;
  }

  let weightInGrams;

  if (limits?.solidNetWeightMultiplier && isSolidEdibleTypeProduct(type, subcategory)) {
    weightInGrams = getProductNetWeightInGrams(product) * quantity;
    return parseFloat(safeToBig(weightInGrams).times(multiplier));
  }

  if (limits?.liquidNetWeightMultiplier && isLiquidTypeProduct(limits, type, subcategory)) {
    weightInGrams = getProductVolumeInGrams(product) * quantity;
    /*
     * Sometimes users of TechPOS will still use the netWeight in measurements rather than volume as suggessted
     * so this just acts as a double check for that scenario
     */

    if (weightInGrams === 0) {
      weightInGrams = getProductNetWeightInGrams(product) * quantity;
    }

    return parseFloat(safeToBig(weightInGrams).times(multiplier));
  }

  weightInGrams =
    isEdible(type) || isTincture(type) || isTopical(type)
      ? getProductTHCInGrams(order)
      : getProductWeight(option, defaultWeight, quantity);

  return parseFloat(safeToBig(weightInGrams).times(multiplier));
};

export const getEdibleTHCInGrams = (order) => {
  const { type } = order.product;
  return isEdible(type) ? getProductTHCInGrams(order) : 0;
};

export const getTinctureTHCInGrams = (order) => {
  const { type } = order.product;
  return isTincture(type) ? getProductTHCInGrams(order) : 0;
};

export const getTopicalTHCInGrams = (order) => {
  const { type } = order.product;
  return isTopical(type) ? getProductTHCInGrams(order) : 0;
};

export const getTotalFlowerInGrams = ({ limits, orders, medical }) =>
  Number(
    _.reduce(
      orders,
      (previous, order) => {
        /**
         * Prevent any excluded subcatgories from triggering state flower limits unless
         * we still need to check equivalency. For example in NV infused pre-rolls are counted as
         * concentrates but need to still count to the overall flower limit
         */
        const { subcategory } = order.product;
        if (_.includes(limits?.ignoreTypeMap?.Flower?.subcategories, subcategory) && !shouldCombine(limits, medical)) {
          return safeToBig(previous);
        }

        return safeToBig(previous).plus(safeToBig(getFlowerEquivalent(order, medical, limits)));
      },
      0
    )
  );

export const getTotalTinctureTHCInGrams = ({ orders }) =>
  Number(_.reduce(orders, (previous, order) => safeToBig(previous).plus(safeToBig(getTinctureTHCInGrams(order))), 0.0));

export const getTotalTopicalTHCInGrams = ({ orders }) =>
  Number(_.reduce(orders, (previous, order) => safeToBig(previous).plus(safeToBig(getTopicalTHCInGrams(order))), 0.0));

export const getTotalEdibleTHCInGrams = ({ orders, limits }) =>
  Number(
    _.reduce(
      orders,
      (previous, order) => {
        const { product } = order;
        const { type, subcategory } = product;
        const edibleCategories = _.get(limits, 'typeMap.Edible.categories', EDIBLE_TYPES);
        const edibleSubcategories = _.get(limits, 'typeMap.Edible.subcategories', []);

        return _.includes(edibleCategories, type) || _.includes(edibleSubcategories, subcategory)
          ? safeToBig(previous).plus(safeToBig(getProductTHCInGrams(order)))
          : safeToBig(previous);
      },
      0.0
    )
  );

export const getTotalConcentrateInGrams = ({ orders, limits, medical, state }) =>
  Number(
    _.reduce(
      orders,
      (previous, order) => {
        const { product, quantity, option } = order;
        const { defaultWeight, type, subcategory } = product;
        const isWeighted = isWeightedProduct(product);
        const concentrateCategories = _.get(limits, 'typeMap.Concentrate.categories', CONCENTRATE_TYPES);
        const concentrateSubcategories = _.get(limits, 'typeMap.Concentrate.subcategories', []);
        const categoryIgnored = _.includes(limits?.ignoreTypeMap?.Concentrate?.categories, subcategory);
        const subcategoryIgnored = _.includes(limits?.ignoreTypeMap?.Concentrate?.subcategories, subcategory);
        const shouldCheckWeightForType = !categoryIgnored && !subcategoryIgnored;

        const isBritishColumbiaDispensary = state === 'BC';
        const flowerEquivalent = getFlowerEquivalent(order, medical, limits);
        const useEquivalent = isBritishColumbiaDispensary && !!flowerEquivalent;

        const productWeight = isWeighted
          ? getProductWeight(option, defaultWeight, quantity)
          : getProductTHCInGrams(order);

        return (shouldCheckWeightForType && _.includes(concentrateCategories, type)) ||
          _.includes(concentrateSubcategories, subcategory)
          ? safeToBig(previous).plus(safeToBig(useEquivalent ? flowerEquivalent : productWeight))
          : safeToBig(previous);
      },
      0.0
    )
  );

export const getSerializedOrders = ({ cart, limits, medical, state }) => {
  const defaultOptionWeights = getComplianceConfig(state, 'defaults');
  const applicableOrders = _.filter(cart, ({ product }) => _.includes(CANNABIS_TYPES, product.type));
  return _.map(applicableOrders, (order) => {
    const { product, quantity, option } = order;
    const { type, subcategory } = product;
    const serializedProduct = {
      ...product,
      multiplier: getMultiplier(type, limits, medical, subcategory),
      defaultWeight: defaultOptionWeights[type],
    };
    return { product: serializedProduct, quantity, option };
  });
};

export const getSerializedCart = ({ state, cart, purchaseState, medical }) => {
  const limits = getStateLimits(state, purchaseState);
  const orders = getSerializedOrders({ cart, limits, medical, state });
  return { orders, state, limits, medical };
};

export const checkFlowerLimit = (cart) => {
  const { limits, medical, state } = cart;
  const { flowerLimit } = getTypeLimits(cart);
  const mixedTypesInCart = cartHasMix(cart);
  const shouldCombineAmounts = shouldCombine(limits, medical);
  const isMixOrder = mixedTypesInCart && shouldCombineAmounts;
  // This was added to allow for product specific limit messages even when equivalency is triggered for
  // orders that are of a single product to improve compliance messaging in Canada - Mack H. 3/15/2021
  const isEquivalencyWithSingleType = !mixedTypesInCart && shouldCombineAmounts;

  let limitMessage = getLimitMessage(`${isMixOrder ? 'cannabis' : 'flower'}`, flowerLimit, state);
  if (isEquivalencyWithSingleType) {
    const productTypeAndSubcategory = _.head(
      _.map(cart.orders, ({ product }) => ({ type: product.type, subcategory: product.subcategory }))
    );
    let productLimit = getLimitForType(productTypeAndSubcategory, cart);
    let typeToDisplay = productTypeAndSubcategory.type;

    if (limits.usesLiquidTypes && isLiquidTypeProduct(limits, typeToDisplay, productTypeAndSubcategory.subcategory)) {
      typeToDisplay = 'liquids';
      productLimit *= GRAMS_PER_OUNCE;
    }
    if (productTypeAndSubcategory.type !== 'Seeds') {
      limitMessage = getLimitMessage(formatDisplayedType(typeToDisplay), productLimit, state);
    } else {
      limitMessage = getLimitMessage('cannabis', flowerLimit, state);
    }
  }

  const totalFlowerInGrams = getTotalFlowerInGrams(cart);

  return totalFlowerInGrams > flowerLimit
    ? {
        type: 'Flower',
        total: totalFlowerInGrams,
        message: limitMessage,
        withinLimits: false,
      }
    : { withinLimits: true };
};

export const checkEdibleTHCLimit = (cart) => {
  const { state } = cart;
  const { edibleTHCLimit } = getTypeLimits(cart);
  const totalEdibleTHCInGrams = getTotalEdibleTHCInGrams(cart);

  return totalEdibleTHCInGrams > edibleTHCLimit
    ? {
        type: 'Edible',
        total: totalEdibleTHCInGrams,
        message: getLimitMessage('edible THC', edibleTHCLimit, state, 'mg'),
        withinLimits: false,
      }
    : { withinLimits: true };
};

export const checkConcentrateLimit = (cart) => {
  const { state } = cart;
  const { concentrateLimit } = getTypeLimits(cart);
  const totalConcentrateInGrams = getTotalConcentrateInGrams(cart);

  return totalConcentrateInGrams > concentrateLimit
    ? {
        type: 'Concentrate',
        total: totalConcentrateInGrams,
        message: getLimitMessage('concentrates', concentrateLimit, state),
        withinLimits: false,
      }
    : { withinLimits: true };
};

export const checkTinctureLimit = (cart) => {
  const { state } = cart;
  const { tinctureLimit } = getTypeLimits(cart);
  const totalTinctureTHCInGrams = getTotalTinctureTHCInGrams(cart);

  return totalTinctureTHCInGrams > tinctureLimit
    ? {
        type: 'Tincture',
        total: totalTinctureTHCInGrams,
        message: getLimitMessage('tinctures', tinctureLimit, state),
        withinLimits: false,
      }
    : { withinLimits: true };
};

export const checkTopicalLimit = (cart) => {
  const { state } = cart;
  const { topicalLimit } = getTypeLimits(cart);
  const totalTopicalTHCInGrams = getTotalTopicalTHCInGrams(cart);

  return totalTopicalTHCInGrams > topicalLimit
    ? {
        type: 'Topicals',
        total: totalTopicalTHCInGrams,
        message: getLimitMessage('topicals', topicalLimit, state),
        withinLimits: false,
      }
    : { withinLimits: true };
};

export const checkLimits = (orderData) => {
  const cart = getSerializedCart(orderData);
  const messageOverride = _.get(cart, 'limits.message', null);

  if (_.isEmpty(cart.orders)) {
    return { withinLimits: true };
  }

  return _.reduce(
    [
      checkFlowerLimit,
      checkEdibleTHCLimit,
      checkConcentrateLimit,
      checkTinctureLimit,
      checkTopicalLimit,
      checkSolidEdibleNetWeight,
      checkLiquidEdibleVolume,
    ],
    (previous, limitChecker) => {
      const result = limitChecker(cart);

      if (messageOverride) {
        result.message = messageOverride;
      }
      if (!result.withinLimits && result.type) {
        result.typesOverLimit = previous.typesOverLimit ? [...previous.typesOverLimit, result.type] : [result.type];
      }

      return !result.withinLimits ? result : previous;
    },
    { withinLimits: true }
  );
};
